import React from 'react';
import { Container, Box, Chip, Breadcrumbs } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { makeStyles } from '@mui/styles';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { RESOURCE_TOGGLE_TYPE, ActivityToggleNav } from './ActivityResourceToggle';
import { AddActivitiesFilter } from './AddActivitiesFilter';
import { useActivityModalProvider } from './ActivityModalProvider';
import ModalsTranslationVariant from 'translations/variants/modals';
import { translate } from 'translations/translator';
import { HwtButton } from '../../Buttons/HwtButton';

const getTranslation = (key: ModalsTranslationVariant) => translate('modals', key as string);

export const AddActivityModalContainer: React.FC<any> = ({ handleAddActivity }) => {
  const [, { closeModal }] = useModal();
  const [{ selectedItems, toggledActivityType }, { setSelectedItems, setToggledActivityType }] =
    useActivityModalProvider();

  const classes = useStyles();

  const handleChange = ids => {
    const getKey = item => (item.type.type === 'clickAway' ? 'clickAwayId' : 'resourceId');

    const items = selectedItems.map(x => ({
      ...x,
      [getKey(x)]: x.id,
      checked: ids.includes(x.id)
    }));

    setSelectedItems(items);
  };

  const handleActivityTypeChange = (_, value: Array<RESOURCE_TOGGLE_TYPE>) =>
    setToggledActivityType(value[0]);

  const onHandleAddActivity = () => {
    handleAddActivity(selectedItems.filter(x => x?.checked === true));
  };

  const withCloseModal = cb => () => {
    cb();
    closeModal();
  };

  const isPinnedTab = toggledActivityType === RESOURCE_TOGGLE_TYPE.PINNED_RESOURCES;

  const columns = [
    {
      headerName: getTranslation(ModalsTranslationVariant.activity),
      field: 'title',
      type: 'string',
      width: 300,
      resizable: false,

      valueGetter({ row: item }) {
        return item.title || item.resource.title;
      }
    },
    {
      headerName: getTranslation(ModalsTranslationVariant.tags),
      filterable: false,
      sortable: false,
      resizable: false,
      width: isPinnedTab ? 300 : 350,
      renderCell({ row: item }): React.ReactElement {
        return (
          <Breadcrumbs separator="" aria-label="breadcrumb" key={`breadcrumb-${item.id}`}>
            {item.tags?.map((tag, idx) => (
              <Chip
                key={`breadcrumb-${idx}`}
                label={tag.value}
                className={classes.styledBreadcrumb}
              />
            )) ||
              item.resource.tags?.map(tag => (
                <Chip key={tag.value} label={tag.value} className={classes.styledBreadcrumb} />
              ))}
          </Breadcrumbs>
        );
      }
    },
    ...(isPinnedTab
      ? [
          {
            headerName: getTranslation(ModalsTranslationVariant.category),
            field: '',
            type: 'string',
            width: 150,
            resizable: false,
            valueGetter() {
              return getTranslation(ModalsTranslationVariant.lwtLibrary);
            }
          }
        ]
      : []),
    {
      headerName: getTranslation(ModalsTranslationVariant.type),
      field: 'type',
      type: 'string',
      width: isPinnedTab ? 150 : 200,
      resizable: false,
      valueGetter({ row: item }) {
        return item?.type?.title || item?.resource?.type?.title;
      }
    }
  ];

  return (
    <Container className={classes.container}>
      <Box
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          marginTop: '0px',
          padding: '10px 32px'
        }}
      >
        <ActivityToggleNav
          handleChange={handleActivityTypeChange}
          toggledActivityType={toggledActivityType}
        />
        <AddActivitiesFilter />
        <Box sx={{ height: 400, width: '100%' }} className={classes.table}>
          <DataGrid
            rows={selectedItems}
            columns={columns as any}
            checkboxSelection
            onRowSelectionModelChange={handleChange}
          />
        </Box>
      </Box>
      <Box display="flex" style={{ margin: '0px 32px 16px 32px', justifyContent: 'space-between' }}>
        <HwtButton
          onClick={closeModal}
          variant="outlined"
          style={{
            width: '106px'
          }}
        >
          {getTranslation(ModalsTranslationVariant.cancel)}
        </HwtButton>
        <HwtButton
          onClick={withCloseModal(onHandleAddActivity)}
          variant="contained"
          disabled={!selectedItems.some(x => x.checked === true)}
        >
          {getTranslation(ModalsTranslationVariant.addActivity)}
        </HwtButton>
      </Box>
    </Container>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex !important',
    flexDirection: 'column',
    marginTop: '0 !important',
    padding: '0 !important',
    height: '663px',
    minWidth: '1050px !important',
    width: '100%',
    backgroundColor: '#fff'
  } as any,
  table: {
    '& .MuiDataGrid-root': {
      border: 'none !important'
    },
    '& .MuiDataGrid-cell': {
      maxHeight: 'max-content !important',
      height: '100%',
      border: 'none !important',
      display: 'flex',
      alignSelf: 'center'
    },
    '& .MuiDataGrid-row': {
      maxHeight: 'max-content !important',
      height: '100%',
      border: '0.5px solid rgba(224, 224, 224, 1)'
    },
    '& .MuiDataGrid-cellContent': {
      textOverflow: 'unset',
      textWrap: 'wrap'
    }
  } as any,
  styledBreadcrumb: {
    borderColor: '#000000 !important',
    color: '#000000 !important',
    textDecoration: 'none !important',
    margin: '5px 0',
    fontWeight: '200 !important',
    fontSize: '14px !important'
  } as any
}));
